import { useId, useMemo, useState } from "react";
import { CircleCheck } from "lucide-react";
import { useAuth } from "@/auth/AuthProvider";
import { buttonClass } from "@/components/buttonStyles";
import { Fact } from "@/components/submissions/Fact";
import { QuoteSubjectivities } from "@/components/submissions/QuoteSubjectivities";
import { formatIsoDate, formatUsd } from "@/lib/displayFormat";
import { saveFile } from "@/lib/downloadFile";
import { messageFor } from "@/lib/errors";
import type { BrokerQuote } from "@/lib/submissions/brokerSubmissions";
import {
  boundFacts,
  boundIds,
  downloadBound,
  type BoundDownload,
} from "@/lib/submissions/boundCoverage";
import { bindStanding } from "@/lib/submissions/quoteBinding";
import { useBoundCoverage } from "@/lib/submissions/useBoundCoverage";

// BRK-S468 — what a bound quote's popup says in place of its Status: Bound (or
// Bound with conditions) and when, the numbers a broker quotes back (binder and
// policy, never an id), the period and the total, the carrier's papers to
// download, and the conditions still open. The quote's own period and total
// show while the binder and policy are read, and stay up if that read fails.
// Built to be reused by BRK-S215's binder and policy pages.

export function BoundSummary({
  quote,
  conditions,
}: {
  quote: BrokerQuote;
  /** Where its open conditions are answered, as on the quote's details;
   *  without it, they don't show. */
  conditions?: {
    submissionId: string;
    accountId: string | null;
    onChanged: () => void | Promise<void>;
  };
}) {
  const titleId = useId();
  const { authedBlob } = useAuth();
  const { binderId, policyId } = boundIds(quote);
  const { read, retry } = useBoundCoverage(binderId, policyId);
  const [downloading, setDownloading] = useState<string | null>(null);
  const [downloadProblem, setDownloadProblem] = useState<string | null>(null);
  const standing = bindStanding(quote);
  const facts = boundFacts(
    quote,
    read.kind === "done" ? read.binder : null,
    read.kind === "done" ? read.policy : null,
  );
  const period = facts.periodFrom
    ? facts.periodTo
      ? `${formatIsoDate(facts.periodFrom)} – ${formatIsoDate(facts.periodTo)}`
      : `From ${formatIsoDate(facts.periodFrom)}`
    : null;
  // Carried to the binder and still to do: open or with Signal (PMS-S560).
  // Kept while the quotes aren't read again: QuoteSubjectivities resets what
  // it holds when its list changes.
  const subjectivities = quote.subjectivities;
  const open = useMemo(
    () =>
      (subjectivities ?? []).filter(
        (s) => s.status === "open" || s.status === "submitted",
      ),
    [subjectivities],
  );

  async function download(paper: BoundDownload) {
    setDownloading(paper.label);
    setDownloadProblem(null);
    try {
      saveFile(await downloadBound(authedBlob, paper), paper.document.filename);
    } catch (err) {
      // A handled line, never a throw or a download that didn't happen (NN #7).
      setDownloadProblem(`Didn’t download: ${messageFor(err)}`);
    } finally {
      setDownloading(null);
    }
  }

  return (
    <section
      aria-labelledby={titleId}
      className="flex flex-col gap-3 rounded-lg border border-brk-border p-4"
    >
      <h3
        id={titleId}
        className="flex items-center gap-2 text-subheading text-brk-text"
      >
        <CircleCheck
          aria-hidden="true"
          className="h-5 w-5 shrink-0 text-green-700 dark:text-green-300"
        />
        {standing?.chip ?? "Bound"}
      </h3>
      {standing && <p className="text-brk-text">{standing.line}</p>}
      <dl className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {period && <Fact label="Policy period" value={period} />}
        {facts.binderNumber && (
          <Fact label="Binder number" value={facts.binderNumber} identifier />
        )}
        {facts.policyNumber && (
          <Fact label="Policy number" value={facts.policyNumber} identifier />
        )}
        {facts.totalCost && (
          <Fact label="Total cost" value={formatUsd(facts.totalCost)} />
        )}
      </dl>
      {read.kind === "loading" && (
        <p role="status" className="text-brk-dim">
          Getting the binder and policy…
        </p>
      )}
      {read.kind === "failed" && (
        <div className="flex flex-col gap-1">
          <p className="text-brk-danger">
            We couldn’t get the binder and policy.
          </p>
          <button
            type="button"
            onClick={retry}
            className={`${buttonClass({ variant: "secondary", size: "sm" })} self-start`}
          >
            Retry
          </button>
        </div>
      )}
      {facts.downloads.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {facts.downloads.map((paper) => (
            <button
              key={paper.label}
              type="button"
              disabled={downloading === paper.label}
              onClick={() => void download(paper)}
              className={buttonClass({ variant: "secondary", size: "sm" })}
            >
              {paper.label}
            </button>
          ))}
        </div>
      )}
      {downloadProblem && (
        <p role="alert" className="text-meta text-brk-danger">
          {downloadProblem}
        </p>
      )}
      {conditions && open.length > 0 && (
        <div className="flex flex-col gap-2">
          <h4 className="text-subheading text-brk-dim">Open conditions</h4>
          <QuoteSubjectivities
            submissionId={conditions.submissionId}
            quoteId={quote.id}
            accountId={conditions.accountId}
            subjectivities={open}
            onChanged={conditions.onChanged}
          />
        </div>
      )}
    </section>
  );
}
